import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { fmtDate, fmtNum, fmtUsd } from "../utils/fmt";
import { useToast } from "../utils/useToast";
import SlotNumber from "../components/SlotNumber";
import CustomSelect from "../components/CustomSelect";

export default function Calculator({ authed }) {
  const [msg, setMsg] = useState("");

  const [weight, setWeight] = useState(1000);
  const [weightUnit, setWeightUnit] = useState("g");

  const [ptPpm, setPtPpm] = useState(0);
  const [pdPpm, setPdPpm] = useState(0);
  const [rhPpm, setRhPpm] = useState(0);

  const [lmeUnit, setLmeUnit] = useState("per_oz");
  const [ptLme, setPtLme] = useState(0);
  const [pdLme, setPdLme] = useState(0);
  const [rhLme, setRhLme] = useState(0);

  const [fxRate, setFxRate] = useState("");

  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const { toast, showToast } = useToast();

  const [whatsapp, setWhatsapp] = useState("");
  const [sendingContact, setSendingContact] = useState(false);

  const [contactCooldownUntil, setContactCooldownUntil] = useState(null); // ISO string

  useEffect(() => {
    if (!authed) return;
    api.myLogs()
    .then((d) => setLogs(d.logs || []))
    .catch((e) => showToast(e.message || "Failed to load history", "err"));
  }, [authed]);

  useEffect(() => {
    const until = localStorage.getItem("contactCooldownUntil");
    if (until) setContactCooldownUntil(until);
  }, []);

  const LS_KEY = "calc:lastInputs:v1";

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}

  useEffect(() => {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return;

  const saved = safeParse(raw);
  if (!saved) return;

  // restore only these fields (as you requested)
  if (saved.lmeUnit) setLmeUnit(saved.lmeUnit);
  if (saved.ptLme != null) setPtLme(saved.ptLme);
  if (saved.pdLme != null) setPdLme(saved.pdLme);
  if (saved.rhLme != null) setRhLme(saved.rhLme);

  // keep fxRate as string to match your controlled input
  if (saved.fxRate != null) setFxRate(String(saved.fxRate));
}, []);

useEffect(() => {
  const payload = {
    lmeUnit,
    ptLme,
    pdLme,
    rhLme,
    fxRate, // keep string
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(LS_KEY, JSON.stringify(payload));
}, [lmeUnit, ptLme, pdLme, rhLme, fxRate]);


  const recent = useMemo(() => (Array.isArray(logs) ? logs.slice(0, 20) : []), [logs]);

  async function requestContact() {
    try {
      setSendingContact(true);
      await api.requestContact(whatsapp);

      // block UI for 24h
      const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      setContactCooldownUntil(until);
      localStorage.setItem("contactCooldownUntil", until);

      showToast("Request sent. Admin will contact you on WhatsApp.", "ok");
      setWhatsapp("");
    } catch (e) {
      // backend tells us exact unblock time
      const until = e?.data?.cooldownUntil;
      if (until) {
        setContactCooldownUntil(until);
        localStorage.setItem("contactCooldownUntil", until);
      }

      showToast(e.message || "Failed to send request", "err");
    } finally {
      setSendingContact(false);
    }
  }

  function loadLogIntoForm(x) {
  const i = x?.input || {};

  setWeight(i.weight ?? 1000);
  setWeightUnit(i.weightUnit || "g");

  setPtPpm(i.ptPpm ?? 0);
  setPdPpm(i.pdPpm ?? 0);
  setRhPpm(i.rhPpm ?? 0);

  setLmeUnit(i.lmeUnit || "per_oz");
  setPtLme(i.lme?.pt ?? 0);
  setPdLme(i.lme?.pd ?? 0);
  setRhLme(i.lme?.rh ?? 0);

  setFxRate(i.fxRate == null ? "" : String(i.fxRate));

  // optional: show saved result instantly
  setResult(x?.result || null);

  showToast("Loaded entry into calculator", "ok");
  window.scrollTo({ top: 0, behavior: "smooth" });
}
  
  async function run() {
    setMsg("");
    setResult(null);

    try {
      const payload = {
        weight: Number(weight),
        weightUnit,
        ptPpm: Number(ptPpm),
        pdPpm: Number(pdPpm),
        rhPpm: Number(rhPpm),
        lmeUnit,
        lme: { pt: Number(ptLme), pd: Number(pdLme), rh: Number(rhLme) },
        fxRate: fxRate === "" ? null : Number(fxRate),
      };

      const d = await api.runCalc(payload);
      console.log("runCalc response", d);
      if (!d?.result) throw new Error("No result returned from server");
      setResult(d.result);
      showToast("Calculation completed", "ok");

      const d2 = await api.myLogs().catch(() => null);
      if (d2?.logs) setLogs(d2.logs);
    } catch (e) {
      showToast(e.message || "Something went wrong", "err");
    }
  }

  if (!authed) {
    return (
      <div className="card">
        <div className="cardHeader">
          <h2>Calculator</h2>
          <div className="smallNote">Login required</div>
        </div>
        <div className="smallNote">Please login to use the calculator.</div>
      </div>
    );
  }

  
  const r = result;
 const grossKg = (() => {
   const w = Number(weight) || 0;
   if (w <= 0) return 0;
   return weightUnit === "kg" ? w : w / 1000;
 })();

 const netKg = r?.netWeightG ? r.netWeightG / 1000 : 0; // keep if you want to show net kg anywhere
 const usdPerKg = grossKg > 0 ? (Number(r?.netUsd || 0) / grossKg) : 0;
  const hasLocal = !!r?.fxRate && r?.netLocal != null;
  const localPerKg = hasLocal && grossKg > 0 ? (Number(r.netLocal) / grossKg) : 0;
  const cooldownMs = contactCooldownUntil ? new Date(contactCooldownUntil).getTime() : 0;
  const contactBlocked = cooldownMs && Date.now() < cooldownMs;

  return (
    <div className="gridCalc">
      
      {/* Inputs */}
      <div className="card">
        <div className="cardHeader">
          <h2>Calculator</h2>
          <div className="smallNote">Pt • Pd • Rh</div>
        </div>

        <div className="form">
          <label>Weight</label>
          <div className="row">
            <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} />
            <CustomSelect
              value={weightUnit}
              onChange={setWeightUnit}
              options={[
                { value: "g", label: "grams" },
                { value: "kg", label: "kg" },
              ]}
            />
          </div>

          <label>PPM</label>
          <div className="row3">
            <input type="number" value={ptPpm} onChange={(e) => setPtPpm(e.target.value)} placeholder="Pt ppm" />
            <input type="number" value={pdPpm} onChange={(e) => setPdPpm(e.target.value)} placeholder="Pd ppm" />
            <input type="number" value={rhPpm} onChange={(e) => setRhPpm(e.target.value)} placeholder="Rh ppm" />
          </div>

          <label>LME input unit</label>
          <CustomSelect
            value={lmeUnit}
            onChange={setLmeUnit}
            options={[
              { value: "per_oz", label: "per ounce" },
              { value: "per_g", label: "per gram" },
            ]}
          />


          <label>LME prices</label>
          <div className="row3">
            <input type="number" value={ptLme} onChange={(e) => setPtLme(e.target.value)} placeholder="Platinum" />
            <input type="number" value={pdLme} onChange={(e) => setPdLme(e.target.value)} placeholder="Palladium" />
            <input type="number" value={rhLme} onChange={(e) => setRhLme(e.target.value)} placeholder="Rhodium" />
          </div>

          <label>USD exchange rate (optional)</label>
          <input
            type="number"
            value={fxRate}
            onChange={(e) => setFxRate(e.target.value)}
            placeholder="If set, shows local price too"
          />

          <button type="button" className="btnPrimary" onClick={run}>
            Calculate
          </button>

          {msg ? <div className="msg">{msg}</div> : null}
        </div>
      </div>

      {/* Results + Recent */}
      <div className="grid" style={{ gap: 14 }}>
        <div className="card">
          <div className="cardHeader">
            <h2>Result</h2>
            <div className="smallNote">{r ? "Calculated" : "—"}</div>
          </div>

          {r ? (
           <div className="appear">
            <>
              <div className="twoCol">
                <div className="statTile">
                  <div className="statLabel">Net USD</div>
                  <div className="statValue">
                    <SlotNumber value={r.netUsd} decimals={2} prefix="$" suffix="/total"/>
                  </div>
                </div>
                 <div className="statTile">
                  <div className="statLabel">USD per kg</div>
                  <div className="statValue">
                    <SlotNumber value={usdPerKg} decimals={2} prefix="$" suffix="/kg" />
                  </div>
                </div>
           {/*
                <div className="statTile">
                  <div className="statLabel">Gross USD</div>
                  <div className="statValue">
                    <SlotNumber value={r.grossUsd} decimals={2} prefix="$" />
                  </div>
                  <div className="statSub">Before charges + margin</div>
                </div>

                <div className="statTile">
                  <div className="statLabel">Charges</div>
                  <div className="statValue">
                    <SlotNumber value={r.chargesUsd} decimals={2} prefix="$" />
                  </div>
                  <div className="statSub">Pt/Pd/Rh charges</div>
                </div>

                <div className="statTile">
                  <div className="statLabel">Margin</div>
                  <div className="statValue">
                    <SlotNumber value={r.marginUsd} decimals={2} prefix="$" />
                  </div>
                  <div className="statSub">Applied margin</div>
                </div>
                */}
              </div>

                {hasLocal ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="twoCol">
                      <div className="statTile">
                        <div className="statLabel">Net Local</div>
                        <div className="statValue">
                          <SlotNumber value={Number(r.netLocal)} decimals={2} />
                        </div>
                        <div className="statSub">Rate {fmtNum(r.fxRate, 4)}</div>
                      </div>

                      <div className="statTile">
                        <div className="statLabel">Local per kg</div>
                        <div className="statValue">
                          <SlotNumber value={localPerKg} decimals={2} suffix="/kg" />
                        </div>
                        <div className="statSub">Normalized by entered kg</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                    <div className="sep" />

                    {contactBlocked ? (
                      <div className="smallNote" style={{ marginTop: 10 }}>
                        Contact request is temporarily disabled.
                        {contactCooldownUntil ? (
                          <> You can request again after {fmtDate(contactCooldownUntil)}.</>
                        ) : null}
                      </div>
                    ) : (
                      <div className="form" style={{ marginTop: 10 }}>
                        <label>Request contact (WhatsApp)</label>

                        <div className="row">
                          <input
                            type="tel"
                            value={whatsapp}
                            onChange={(e) => setWhatsapp(e.target.value)}
                            placeholder="+00..."
                          />

                          <button
                            type="button"
                            className="btnPrimary"
                            onClick={requestContact}
                            disabled={!whatsapp.trim() || sendingContact}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {sendingContact ? "Sending..." : "Request"}
                          </button>
                        </div>

                        <div className="smallNote" style={{ marginTop: 8 }}>
                          Admins will receive a push notification and contact you on WhatsApp.
                        </div>
                      </div>
                    )}
                
                
              <div className="sep" />

              <div className="tableWrap">
                <table className="table" style={{ minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th>Metal</th>
                      <th>Recovered (g)</th>
                      <th>Recovered (oz)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Platinum</td>
                      <td>{fmtNum(r.recoveredG?.pt, 4)}</td>
                      <td>{fmtNum(r.recoveredOz?.pt, 6)}</td>
                    </tr>
                    <tr>
                      <td>Palladium</td>
                      <td>{fmtNum(r.recoveredG?.pd, 4)}</td>
                      <td>{fmtNum(r.recoveredOz?.pd, 6)}</td>
                    </tr>
                    <tr>
                      <td>Rhodium</td>
                      <td>{fmtNum(r.recoveredG?.rh, 4)}</td>
                      <td>{fmtNum(r.recoveredOz?.rh, 6)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
            </div>
            ) : (
            <div className="smallNote">Run a calculation to see results.</div>
            )}
        </div>

        <div className="card">
          <div className="cardHeader">

            <h2>Recent checks</h2>
            <div className="smallNote">{recent.length ? `${recent.length} shown` : "—"}</div>
          </div>

            {recent.length ? (
              <>
                {/* Desktop table */}
                <div className="desktopOnly">
                  <div className="tableWrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Weight</th>
                          <th>PPM (Pt/Pd/Rh)</th>
                          <th>Net USD</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((x) => (
                          <tr key={x._id}>
                            <td>{fmtDate(x.createdAt)}</td>
                            <td>
                              {fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}
                            </td>
                            <td className="wrapCell">
                              {fmtNum(x.input?.ptPpm, 0)} / {fmtNum(x.input?.pdPpm, 0)} / {fmtNum(x.input?.rhPpm, 0)}
                            </td>
                            <td style={{ fontWeight: 800 }}>{fmtUsd(x.result?.netUsd)}</td>
                            <td style={{ textAlign: "right" }}>
                              <button type="button" className="btnPrimary" onClick={() => loadLogIntoForm(x)}>
                                Load
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile stacked rows */}
                <div className="mobileOnly">
                  {recent.map((x) => (
                    <div className="mRow" key={x._id}>
                      <div className="mRowTop">
                        <div className="mRowTitle">{fmtUsd(x.result?.netUsd)}</div>
                        <div className="mRowMeta">{fmtDate(x.createdAt)}</div>
                        <div style={{ marginTop: 10 }}>
                          <button type="button" className="btnPrimary" onClick={() => loadLogIntoForm(x)}>
                            Load
                          </button>
                        </div>
                      </div>

                      <div className="mGrid">
                        <div className="mField">
                          <div className="mLabel">Weight</div>
                          <div className="mValue">
                            {fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}
                          </div>
                        </div>
                        <div className="mField">
                          <div className="mLabel">PPM</div>
                          <div className="mValue">
                            Pt {fmtNum(x.input?.ptPpm, 0)} • Pd {fmtNum(x.input?.pdPpm, 0)} • Rh {fmtNum(x.input?.rhPpm, 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="smallNote">No history yet.</div>
            )}
        </div>
        {toast ? (
        <div className={`toast ${toast.type === "err" ? "toastErr" : "toastOk"}`}>
          {toast.text}
        </div>
      ) : null}
      </div>
    </div>
  );
}